import { Flex, Spin, Typography } from "antd";
import { observer } from "mobx-react-lite";
import { useState } from "react";

import { DocUploadedResponse, FILE_TYPE_ENUM, NOTIFICATION_TYPE_ENUM, S3FileType } from "interfaces";
import { useStores } from "stores";
import { CountBadge, EditableFile, FileInput, WhiteBoxBlock } from "ui";
import { removeBidFile, uploadBidFile } from "services";
import { useNotificationContext } from "providers";
import { fileExistsInObject, parseErrorMessage } from "utils";
import { FileRule } from "interfaces";

interface BidFilesBlockProps {
  totalDocumentsCount: number;
  showReports: boolean;
  showAdditionalDocs: boolean;
  fileRules: FileRule[];
}

export const BidFilesBlock = observer(
  ({ totalDocumentsCount, showReports, showAdditionalDocs, fileRules }: BidFilesBlockProps) => {
    const { bidCard, user } = useStores();
    const { openNotification } = useNotificationContext();

    const [fileLoading, setFileLoading] = useState(false);

    const { isJuniorOperator, isSeniorOperator } = user.roles;
    const { id, documents, reports } = bidCard.bid;

    const currencyControlFile = reports[0];
    const internalAccountingFile = reports[1];

    const isOperator = isSeniorOperator || isJuniorOperator;

    const fileOnFinishHandler = () => {
      setTimeout(() => {
        setFileLoading(false);
        bidCard.updateCardById(id);
      }, 1500);
    };

    const handleUploadFile = async (responseUpload: DocUploadedResponse[]) => {
      setFileLoading(true);
      await uploadBidFile({ requestId: id }, { docIds: responseUpload.map((item) => item.id) })
        .then(() => {
          fileOnFinishHandler();
        })
        .catch((e) => {
          setFileLoading(false);
          throw e;
        });
    };

    const handleRemoveFile = async (fileId: string) => {
      setFileLoading(true);
      return await removeBidFile({ requestId: id, documentId: fileId })
        .then(() => {
          openNotification({
            message: "Документ удален",
            type: NOTIFICATION_TYPE_ENUM.SUCCESS,
          });
          fileOnFinishHandler();
        })
        .catch((e) => {
          setFileLoading(false);
          openNotification({
            description: parseErrorMessage(e),
            type: NOTIFICATION_TYPE_ENUM.ERROR,
          });
        });
    };

    const renderFileInputs = () => {
      if (!fileRules || !Array.isArray(fileRules)) {
        return [];
      }
      return fileRules.map(({ type, label, canUpload, visible, multipleUpload, canRemove }) => {
        const fileExistsInDocuments = fileExistsInObject(documents, type);
        const showFileInput =
          canUpload && (!fileExistsInDocuments || (fileExistsInDocuments && multipleUpload.canLoadAgain));

        return (
          (fileExistsInDocuments || canUpload) &&
          visible && (
            <Flex vertical gap="small" key={type}>
              {fileExistsInDocuments && (
                <Flex vertical gap="small">
                  {documents[type].map((item) => (
                    <EditableFile
                      tooltip={label}
                      showRemoveIcon={canRemove}
                      file={item}
                      removeFileHandler={() => handleRemoveFile(item.id)}
                      key={item.id}
                    />
                  ))}
                </Flex>
              )}
              {showFileInput && (
                <FileInput
                  fileType={type as S3FileType}
                  onUpload={handleUploadFile}
                  blockTitle={label}
                  multiple={fileExistsInDocuments}
                  isContinue={fileExistsInDocuments}
                />
              )}
            </Flex>
          )
        );
      });
    };

    const additionalDocumentsList = bidCard.bid.additionalDocuments[FILE_TYPE_ENUM.ADDITIONAL_DOC];

    const renderAdditionalDocs = () => {
      return (
        additionalDocumentsList &&
        additionalDocumentsList.length > 0 && (
          <Flex vertical gap="small">
            <Typography.Text strong>Дополнительные документы</Typography.Text>
            {additionalDocumentsList.map((item) => (
              <EditableFile
                showTooltip={false}
                showRemoveIcon={isSeniorOperator}
                file={item}
                removeFileHandler={() => handleRemoveFile(item.id)}
                key={item.id}
              />
            ))}
          </Flex>
        )
      );
    };

    const additionalDocs = renderAdditionalDocs();
    const documentBlock = renderFileInputs();

    const reportsBlock = (
      <>
        <Typography.Text strong>Справки о сделке</Typography.Text>

        {currencyControlFile && (
          <EditableFile tooltip="Для валютного контроля" showRemoveIcon={false} file={currencyControlFile} />
        )}
        {internalAccountingFile && (
          <EditableFile tooltip="Для внутренного учета" showRemoveIcon={false} file={internalAccountingFile} />
        )}
      </>
    );

    const fileBlockHeader = (
      <Flex align="center">
        <Flex gap={4} align="center">
          <Typography.Text className="whitebox-title">Документы</Typography.Text>
          {totalDocumentsCount > 0 && <CountBadge count={totalDocumentsCount} />}
        </Flex>
      </Flex>
    );

    const addFileInput = (
      <FileInput
        blockTitle={additionalDocumentsList?.length > 0 ? undefined : "Дополнительные документы"}
        fileType={FILE_TYPE_ENUM.ADDITIONAL_DOC}
        onUpload={handleUploadFile}
        isContinue={additionalDocumentsList?.length > 0}
        smallSize={totalDocumentsCount > 0}
      />
    );

    if (documentBlock.filter(Boolean).length === 0) {
      if (showReports) {
        return (
          <WhiteBoxBlock>
            <Flex vertical gap="middle">
              {fileBlockHeader}
              <Flex vertical>{reportsBlock}</Flex>
              {isOperator && addFileInput}
            </Flex>
          </WhiteBoxBlock>
        );
      }

      return (
        <WhiteBoxBlock>
          {fileBlockHeader}
          <br />
        </WhiteBoxBlock>
      );
    }

    const documentsContent = (
      <Flex vertical gap="small">
        {documentBlock}
        <Flex vertical gap="small" className="margin-top-sm">
          {showAdditionalDocs && additionalDocs}
          {isOperator && addFileInput}
        </Flex>
        {showReports && reportsBlock}
      </Flex>
    );

    return (
      <Spin spinning={fileLoading} className="padding-md">
        <WhiteBoxBlock>
          <Flex vertical gap="middle" id="bid-documents">
            {fileBlockHeader}
            {documentsContent}
          </Flex>
        </WhiteBoxBlock>
      </Spin>
    );
  }
);
